import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { updateWorkspaceUser } from "@app/common/account/api";
import useAuth from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import * as Events from "@app/common/workspaces/events";
import type {
  EnterpriseWorkspace,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";

interface TransferWorkspaceOwnershipRequest {
  workspaceId: EnterpriseWorkspace["id"];
  newOwnerUserId: EnterpriseWorkspaceUser["userId"];
}

const useTransferWorkspaceOwnership = () => {
  const { fetchAndSetUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (request: TransferWorkspaceOwnershipRequest) => {
      Events.transferWorkspaceOwnership({ workspaceId: request.workspaceId });
      return updateWorkspaceUser(request.workspaceId, {
        userId: request.newOwnerUserId,
        role: "owner",
      });
    },
    onSuccess: async (data, { workspaceId }) => {
      await Promise.all([
        fetchAndSetUser(),
        queryClient.invalidateQueries({ queryKey: ["workspace"] }),
      ]);
      navigate(ROUTES.workspaceSettingsById(workspaceId));
      toast.success("Ownership has been successfully transferred!");
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.error(
        "Something went wrong when transferring ownership. Please refresh the page or contact support@read.ai if the problem persists."
      );
    },
  });
};

export default useTransferWorkspaceOwnership;
